import { Badge, Button, HStack, Icon } from '@chakra-ui/react'
import { faCheck, faPen, faPlus, faUndo, faXmark } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'

interface EditableGridButtonsProps<TRow extends { id: any }> {
    addRow: () => void
    isSubmitting: boolean
    hasChanges: boolean
    cancelChanges: () => void
    handleSubmit: () => Promise<any> | void
    created: TRow[]
    edited: TRow[]
    deletedIds: TRow['id'][]
}

export const EditableGridButtons = <TRow extends { id: any }>({
    addRow,
    isSubmitting,
    hasChanges,
    cancelChanges,
    handleSubmit,
    created,
    edited,
    deletedIds
}: EditableGridButtonsProps<TRow>) => {
    const { translate } = useTranslationsContext()

    return <HStack gap={2}>
        <Button
            size="sm"
            onClick={addRow}
            isDisabled={isSubmitting}
            h={6}
            px={3}>
            <HStack>
                <Icon as={FontAwesomeIcon} icon={faPlus} />
                <span>{translate('editableGrid.add', 'Přidat')}</span>
            </HStack>
        </Button>
        <Button
            size="sm"
            colorScheme='gray'
            onClick={cancelChanges}
            isDisabled={!hasChanges || isSubmitting}
            h={6}
            px={3}
        >
            <HStack>
                <Icon as={FontAwesomeIcon} icon={faUndo} />
                <span>{translate('editableGrid.revert', 'Revert')}</span>
            </HStack>
        </Button>
        <Button
            size="sm"
            type="button"
            isLoading={isSubmitting}
            isDisabled={!hasChanges}
            onClick={handleSubmit}
            h={6}
            px={3}
        >
            <HStack>
                <Icon as={FontAwesomeIcon} icon={faCheck} />
                <span>{translate('editableGrid.commit', 'Commit')}</span>
            </HStack>
        </Button>

        {hasChanges && (
            <HStack
                h={6}
                borderRadius='sm'
                borderColor="gray.400"
                gap={2}
            >
                <StatBadge icon={faPlus} count={created.length} color="green" />
                <StatBadge icon={faPen} count={edited.length} color="orange" />
                <StatBadge icon={faXmark} count={deletedIds.length} color="red" />
            </HStack>
        )}
    </HStack>
}

const StatBadge = ({ icon, count, color }: { icon: any; count: number, color: string }) => {
    if (count === 0) return null

    return (
        <Badge h={6} px={2.5} colorScheme={color} border="1px solid" alignContent='center' borderRadius={'sm'}
            borderColor={`${color}.100`} color={`${color}.600`} bg={`${color}.100`}
        >
            <HStack gap={1.5}>
                <Icon as={FontAwesomeIcon} icon={icon} size='sm' color={`${color}.600`} />
                <span>{count}</span>
            </HStack>
        </Badge>
    )
}
